import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Button,
  Window,
  WindowHeader,
  WindowContent,
  Fieldset,
  TextField,
} from 'react95';
import { useState, useEffect } from 'react';
import { PublicKey } from '@solana/web3.js';
import { supabase } from './supabaseClient';

export default function ArtworkGrid({ artworks }) {
  // State for modals
  const [selectedArt, setSelectedArt] = useState(null);
  const [voteArt, setVoteArt] = useState(null);
  const [trackingArt, setTrackingArt] = useState(null);
  const [voterWallet, setVoterWallet] = useState('');
  const [voterRecords, setVoterRecords] = useState({});

  const validateSolanaWallet = (w) => {
    try {
      const pk = new PublicKey(w);
      return PublicKey.isOnCurve(pk);
    } catch {
      return false;
    }
  };

  // Fetch voter records when the component mounts
  useEffect(() => {
    async function fetchVotes() {
      const { data } = await supabase
        .from('votes')
        .select('artwork_id, voter_wallet');

      const records = {};
      (data || []).forEach(({ artwork_id, voter_wallet }) => {
        records[artwork_id] = records[artwork_id] || [];
        records[artwork_id].push(voter_wallet);
      });

      setVoterRecords(records);
    }

    fetchVotes();
  }, []);

  // Handle vote submission
  const handleVoteSubmit = async () => {
    if (!voterWallet) {
      alert('Please enter your wallet address.');
      return;
    }

    if (!validateSolanaWallet(voterWallet)) {
      alert('❌ Invalid wallet. Use a real Solana address.');
      return;
    }

    try {
      const { error } = await supabase
        .from('votes')
        .insert([{ artwork_id: voteArt.id, voter_wallet: voterWallet }]);

      if (error) {
        alert(error.code === '23505' ? 'You already voted.' : 'Vote error.');
      } else {
        alert('✅ Vote recorded!');
        // Refresh voter records after voting
        const { data } = await supabase
          .from('votes')
          .select('artwork_id, voter_wallet');

        const records = {};
        (data || []).forEach(({ artwork_id, voter_wallet }) => {
          records[artwork_id] = records[artwork_id] || [];
          records[artwork_id].push(voter_wallet);
        });

        setVoterRecords(records);
      }
    } catch (error) {
      console.error('Error submitting vote:', error);
      alert('An error occurred while submitting your vote.');
    }

    setVoteArt(null);
    setVoterWallet('');
  };

  return (
    <div className='listGrid'>
      {artworks.map((art, idx) => (
        <motion.div
          className='listItem'
          key={art.id}
          whileHover={{ scale: 1.05 }}
        >
          <div className='rank-badge'>#{idx + 1}</div>
          <img
            src={art.image_url}
            alt={art.title}
            onClick={() => setSelectedArt(art)}
          />
          <div className='tipTitle'>
            <Link to={`/Anime-Art-Hub/artist/${art.wallet}`}>
              {art.title || 'Unknown Artist'}
            </Link>
          </div>
          <div className='tipDesc'>{art.description}</div>
          <div className='voteCount'>
            💖 {voterRecords[art.id]?.length || 0} votes
          </div>
          <Button size='sm' onClick={() => setVoteArt(art)}>
            👍 Vote
          </Button>
          <Button
            size='sm'
            onClick={() => {
              navigator.clipboard.writeText(art.wallet);
              alert('Artist wallet copied!');
            }}
          >
            💸 Tip
          </Button>
          <Button size='sm' onClick={() => setTrackingArt(art)}>
            🔍 Track Votes
          </Button>
          <Button
            size='sm'
            onClick={() => {
              const text = `🚨 Check out this #AnimeArt on #AnimeArtHub! 🖼️💫
              Vote for it now on the official hub!
              👇👇👇
              Art by: ${encodeURIComponent(art.title)}`;
              const tweetLink = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(`https://solana.animeco.in/Anime-Art-Hub/artwork/${art.id}`)}&hashtags=Animecoin`;
              window.open(tweetLink, '_blank');
            }}
          >
            🐦 Share Art on X
          </Button>
        </motion.div>
      ))}

      {/* Image Preview Modal */}
      {selectedArt && (
        <div className='modal-backdrop'>
          <Window className='modal-window'>
            <WindowHeader className='modal-header'>
              Artwork
              <Button size='sm' onClick={() => setSelectedArt(null)}>
                X
              </Button>
            </WindowHeader>
            <WindowContent>
              <img
                src={selectedArt.image_url}
                alt={selectedArt.title}
                className='modal-image'
              />
              <Link to={`/Anime-Art-Hub/artwork/${selectedArt.id}`}>
                {selectedArt.description || 'Untitled'}
              </Link>
            </WindowContent>
          </Window>
        </div>
      )}

      {/* Vote Modal */}
      {voteArt && (
        <div className='modal-backdrop'>
          <Window className='modal-window'>
            <WindowHeader className='modal-header'>
              Vote for "{voteArt.description}"
              <Button size='sm' onClick={() => setVoteArt(null)}>
                X
              </Button>
            </WindowHeader>
            <WindowContent>
              <Fieldset label='Your Solana Wallet'>
                <TextField
                  fullWidth
                  value={voterWallet}
                  onChange={(e) => setVoterWallet(e.target.value)}
                />
              </Fieldset>
              <Button onClick={handleVoteSubmit}>✅ Vote</Button>
            </WindowContent>
          </Window>
        </div>
      )}

      {/* Vote Tracker Modal */}
      {trackingArt && (
        <div className='modal-backdrop'>
          <Window className='modal-window'>
            <WindowHeader className='modal-header'>
              Vote Tracker — {trackingArt.description}
              <Button size='sm' onClick={() => setTrackingArt(null)}>
                X
              </Button>
            </WindowHeader>
            <WindowContent>
              <ul>
                {(voterRecords[trackingArt.id] || []).map((w, i) => (
                  <li key={i}>
                    <a
                      href={`https://solscan.io/account/${w}`}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      {w.slice(0, 6)}...{w.slice(-4)}
                    </a>
                  </li>
                ))}
                {(!voterRecords[trackingArt.id] ||
                  voterRecords[trackingArt.id].length === 0) && (
                  <p>No votes yet.</p>
                )}
              </ul>
            </WindowContent>
          </Window>
        </div>
      )}
    </div>
  );
}